import { IconMapPin, IconPhone, IconStarFilled } from '@tabler/icons-react';
import { useTranslation } from '../../../../i18n/hooks/useTranslation';
import { PosterLogo, PosterSheet, fs, hexToRgba, posterFields, valueFitScale } from '../posterParts';

/**
 * "Official notice" — a government-circular look: cream paper, serif type, a double ink border with corner blocks, an emblem medallion,
 * a folded ribbon for the notice title and a ruled information table. Palette: paper, paperDeep, ink, accent, ribbon, text, danger.
 */
const SERIF = "'Noto Serif Bengali', 'Tiro Bangla', 'Hind Siliguri', serif";

// A ribbon with folded tails, as one SVG (its shape scales with the width; the title is laid over the main band).
function Ribbon({ p, children }) {
    return (
        <div className="relative mx-auto" style={{ width: '76cqw' }}>
            <svg viewBox="0 0 300 58" width="100%" style={{ display: 'block', height: 'auto' }} aria-hidden="true">
                <polygon points="0,12 40,12 40,54 0,54 14,33" fill={p.ribbonDark} />
                <polygon points="300,12 260,12 260,54 300,54 286,33" fill={p.ribbonDark} />
                <polygon points="22,42 40,54 40,42" fill="#000" fillOpacity="0.35" />
                <polygon points="278,42 260,54 260,42" fill="#000" fillOpacity="0.35" />
                <rect x="22" y="0" width="256" height="42" fill={p.ribbon} />
                <rect x="26" y="4" width="248" height="34" fill="none" stroke={p.accent} strokeWidth="1.2" strokeDasharray="3 2.2" />
            </svg>
            <div className="absolute left-[7.3%] right-[7.3%] top-0 flex items-center justify-center" style={{ height: '72.4%' }}>
                {children}
            </div>
        </div>
    );
}

function Row({ p, label, children, first = false }) {
    return (
        <div className="grid" style={{ gridTemplateColumns: '31% 1fr', borderTop: first ? 'none' : `0.2cqw solid ${hexToRgba(p.ink, 0.55)}` }}>
            <div
                className="flex items-center"
                style={{ background: hexToRgba(p.ink, 0.09), borderRight: `0.25cqw solid ${p.ink}`, padding: `${fs(1.5)} ${fs(2)}`, color: p.ink, fontSize: fs(3), fontWeight: 800, lineHeight: 1.2 }}
            >
                {label}
            </div>
            <div className="flex items-center" style={{ padding: `${fs(1.5)} ${fs(2.4)}`, color: p.text, fontSize: fs(3.5), fontWeight: 700, lineHeight: 1.3 }}>
                {children}
            </div>
        </div>
    );
}

export default function OfficialDesign({ job, business, theme, showFooter = true, className = '' }) {
    const { t } = useTranslation();
    const p = theme.palette;
    const f = posterFields(job, business, t);
    const eduScale = valueFitScale(f.edu, 46);
    const ageScale = valueFitScale(f.age, 34);

    const body = (
        <div className="flex flex-1 flex-col justify-center" style={{ padding: `${fs(8.5)} 9.5cqw ${fs(3)}`, gap: fs(2.1) }}>
            {/* letterhead */}
            <div className="flex flex-col items-center" style={{ gap: fs(1.8) }}>
                <div
                    className="flex items-center justify-center rounded-full"
                    style={{ width: fs(17), height: fs(17), background: '#fff', border: `${fs(0.55)} solid ${p.accent}`, boxShadow: `0 0 0 ${fs(0.5)} ${p.paper}, 0 0 0 ${fs(0.85)} ${p.ink}` }}
                >
                    <PosterLogo job={job} size={12.6} />
                </div>
                <div className="flex items-center justify-center" style={{ gap: fs(1.6) }}>
                    <div style={{ height: '0.25cqw', width: '20cqw', background: `linear-gradient(90deg, transparent, ${p.accent})` }} />
                    <div style={{ width: fs(1.5), height: fs(1.5), background: p.accent, transform: 'rotate(45deg)' }} />
                    <div style={{ height: '0.25cqw', width: '20cqw', background: `linear-gradient(270deg, transparent, ${p.accent})` }} />
                </div>
                <div className="text-center" style={{ color: p.ink, fontSize: fs(6.4), fontWeight: 800, lineHeight: 1.22, textShadow: '0 0.25cqw 0 rgba(255,255,255,0.75)' }}>
                    {f.org}
                </div>
                {f.position && (
                    <div className="text-center" style={{ color: p.text, opacity: 0.85, fontFamily: "'Tiro Bangla', 'Noto Serif Bengali', serif", fontStyle: 'italic', fontSize: fs(3.7), lineHeight: 1.3 }}>
                        {f.position}
                    </div>
                )}
            </div>

            <Ribbon p={p}>
                <div className="text-center" style={{ color: '#fff', fontSize: fs(4.5), fontWeight: 800, letterSpacing: '0.04em', textShadow: '0 0.25cqw 0.4cqw rgba(0,0,0,0.35)' }}>
                    {f.label.notice}
                </div>
            </Ribbon>

            {/* the ruled table */}
            <div className="overflow-hidden" style={{ border: `0.4cqw solid ${p.ink}`, borderRadius: '1cqw', background: 'rgba(255,255,255,0.55)', boxShadow: '0 0.5cqw 1.2cqw rgba(0,0,0,0.12)' }}>
                <Row p={p} label={f.label.vacancy} first>
                    <div style={{ color: p.danger, fontSize: fs(8.2), fontWeight: 900, lineHeight: 1 }}>{f.vacancy}</div>
                    <div style={{ color: p.ink, fontSize: fs(3.4), fontWeight: 800, paddingLeft: fs(1.4), paddingTop: fs(2.2) }}>{f.label.unit}</div>
                </Row>
                <Row p={p} label={f.label.edu}>
                    <div style={{ fontSize: `calc(${3.5 * eduScale}cqw * var(--fs, 1))` }}>{f.edu}</div>
                </Row>
                <Row p={p} label={f.label.age}>
                    <div style={{ fontSize: `calc(${3.5 * ageScale}cqw * var(--fs, 1))` }}>{f.age}</div>
                </Row>
                <Row p={p} label={f.label.start}>{f.start}</Row>
                <Row p={p} label={f.label.end}>
                    <div style={{ background: p.danger, color: '#fff', borderRadius: '99px', padding: `${fs(0.3)} ${fs(2.4)}`, fontWeight: 800 }}>{f.end}</div>
                </Row>
            </div>

            <div className="text-center" style={{ color: p.text, fontSize: fs(2.6), fontWeight: 600, fontStyle: 'italic' }}>✓ {f.label.promo}</div>

            {/* contact */}
            <div style={{ border: `0.3cqw solid ${p.ink}`, borderRadius: '1.3cqw', padding: '0.7cqw' }}>
                <div className="flex items-center justify-center" style={{ background: p.ink, borderRadius: '0.8cqw', color: '#fff', padding: `${fs(1.7)} ${fs(2)}`, gap: fs(2.2), fontSize: fs(4.2), fontWeight: 800 }}>
                    <IconStarFilled style={{ width: fs(2.4), height: fs(2.4), color: p.accent }} />
                    <div>{f.label.cta}</div>
                    <IconStarFilled style={{ width: fs(2.4), height: fs(2.4), color: p.accent }} />
                </div>
            </div>
        </div>
    );

    const footer = showFooter ? (
        <div className="text-center" style={{ margin: '0 4.5cqw 4.5cqw', borderTop: `0.9cqw double ${p.ink}`, background: hexToRgba(p.ink, 0.08), padding: `${fs(2.4)} 5cqw ${fs(3)}` }}>
            <div className="flex items-center justify-center" style={{ gap: fs(2.2) }}>
                {f.shop.logo && (
                    <img src={f.shop.logo} alt="" className="flex-none rounded-full object-contain" style={{ width: fs(8.5), height: fs(8.5), background: '#fff', border: `0.4cqw solid ${p.accent}`, padding: '0.6cqw' }} />
                )}
                <div style={{ color: p.ink, fontSize: fs(6), fontWeight: 800, lineHeight: 1.15, opacity: f.shop.placeholder ? 0.6 : 1 }}>{f.shop.nameShown}</div>
            </div>
            <div className="flex items-center justify-center" style={{ gap: fs(1), marginTop: fs(0.9), color: p.text, fontSize: fs(3.6), lineHeight: 1.25, opacity: f.shop.addressPlaceholder ? 0.6 : 1 }}>
                <IconMapPin style={{ width: fs(3.6), height: fs(3.6), color: p.accent }} className="flex-none" />
                <div>{f.shop.addressShown}</div>
            </div>
            {f.shop.phone && (
                <div className="flex items-center justify-center" style={{ gap: fs(1), marginTop: fs(0.7), color: p.ink, fontSize: fs(4.4), fontWeight: 800 }}>
                    <IconPhone style={{ width: fs(4), height: fs(4), color: p.accent }} className="flex-none" />
                    <div>{f.shop.phone}</div>
                </div>
            )}
        </div>
    ) : null;

    // the frame (double ink border) and its four corner blocks sit on top of everything
    const corner = (pos) => (
        <div className="absolute" style={{ ...pos, width: '3.4cqw', height: '3.4cqw', background: p.paper, border: `0.5cqw solid ${p.ink}` }}>
            <div style={{ position: 'absolute', inset: '0.7cqw', background: p.accent, transform: 'rotate(45deg) scale(0.7)' }} />
        </div>
    );
    const overlay = (
        <>
            <div className="pointer-events-none absolute" style={{ inset: '1.8cqw', border: `0.8cqw solid ${p.ink}`, borderRadius: '0.5cqw', opacity: 0.92 }} />
            <div className="pointer-events-none absolute" style={{ inset: '3.5cqw', border: `0.22cqw solid ${p.ink}`, opacity: 0.75 }} />
            {corner({ left: '0.1cqw', top: '0.1cqw' })}
            {corner({ right: '0.1cqw', top: '0.1cqw' })}
            {corner({ left: '0.1cqw', bottom: '0.1cqw' })}
            {corner({ right: '0.1cqw', bottom: '0.1cqw' })}
        </>
    );

    return (
        <PosterSheet
            deps={[job, business, theme, showFooter, t]}
            className={className}
            fontFamily={SERIF}
            background={`radial-gradient(120% 70% at 50% 0%, rgba(255,255,255,0.65), transparent 60%), linear-gradient(180deg, ${p.paper} 0%, ${p.paperDeep} 100%)`}
            body={body}
            footer={footer}
            overlay={overlay}
        />
    );
}
